import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { SearchSelect, type ComboboxItem } from '@/components/ui'
import { searchParties, type Party } from '../api/queries'

export interface CustomerContactPickerProps {
  /** Display label for the current selection (contact or company name). */
  label: string | null
  onPick: (party: Party) => void
  /** Clears the current selection. When provided, a clear (×) affordance shows. */
  onClear?: () => void
  placeholder?: string
  width?: number
  className?: string
}

/**
 * One search box over BOTH companies and people. Type a company → pick the
 * company; type a person → pick the contact (the form auto-fills the
 * company). Server-side search via /api/sample-orders/search-parties.
 *
 * Own copy, not imported from the Uviva sample-requests module — cross-
 * module imports are banned (ops/CLAUDE.md); this mirrors that module's
 * CustomerContactPicker 1:1 for SPI's own commercial-sample intake.
 */
export function CustomerContactPicker({
  label,
  onPick,
  onClear,
  placeholder = 'Search a company or a person…',
  width = 460,
  className,
}: CustomerContactPickerProps) {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 200)
    return () => clearTimeout(id)
  }, [query])

  const { data, isFetching } = useQuery({
    queryKey: ['sample-orders', 'party-search', debounced],
    queryFn: () => searchParties(debounced),
    enabled: debounced.trim().length > 0,
    staleTime: 30_000,
  })

  const parties = data ?? []

  const items: ComboboxItem[] = useMemo(
    () =>
      parties.map((p) => {
        const extras =
          p.kind === 'contact'
            ? [p.company_name, p.email].filter(Boolean).join(' · ')
            : ''
        return {
          value: `${p.kind}:${p.id}`,
          label: extras ? `${p.name} · ${extras}` : p.name,
          group: p.kind === 'contact' ? 'Contacts' : 'Customers',
        }
      }),
    [parties],
  )

  const handlePicked = (val: string) => {
    const party = parties.find((p) => `${p.kind}:${p.id}` === val)
    if (!party) return
    onPick(party)
    setQuery('')
  }

  const emptyMessage = isFetching
    ? 'Searching…'
    : debounced
      ? 'No matches'
      : 'Type a company or contact name'

  return (
    <SearchSelect
      variant="field"
      items={items}
      selectedLabel={label}
      onQueryChange={setQuery}
      onSelect={handlePicked}
      onClear={onClear}
      clearable={!!onClear}
      width={width}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      shouldFilter={false}
      className={className}
    />
  )
}
